/**
 * User Management DTOs and Types
 * Defines all request/response types for user management APIs
 *
 * @module Features/UserManagement/Types
 */

/**
 * User role enumeration
 */
export enum UserRole {
  Admin = 'Admin',
  Clerk = 'Clerk',
}

/**
 * User list DTO with minimal information for table display
 */
export interface UserListDto {
  id: number;
  email: string;
  firstName: string | null;
  lastName: string | null;
  roles: string[];
  isActive: boolean;
  emailConfirmed: boolean;
  createdAt: string;
  updatedAt: string;
}

/**
 * User details DTO with full information
 */
export interface UserDetailsDto {
  id: number;
  email: string;
  firstName: string | null;
  lastName: string | null;
  roles: string[];
  isActive: boolean;
  emailConfirmed: boolean;
  mustChangePassword: boolean;
  failedLoginAttempts: number;
  lockedOutUntil: string | null;
  lastLoginAt: string | null;
  createdAt: string;
  updatedAt: string;
}

/**
 * Request to create new user
 * Supports both local (no verification) and remote (with verification) flows
 */
export interface CreateUserRequest {
  email: string;
  firstName: string;
  lastName: string;
  roles: string[];
  initialPassword?: string; // If not provided, system generates one
  sendWelcomeEmail: boolean;
  requireEmailVerification: boolean; // Toggle between local/remote flow
}

/**
 * Response after user creation
 * Different for local vs remote flow
 */
export interface CreateUserResponse {
  userId: number;
  email: string;
  roles: string[];
  temporaryPassword?: string; // Present in local flow only
  emailVerificationRequired: boolean;
  message: string;
}

/**
 * Request to update existing user
 */
export interface UpdateUserRequest {
  email: string;
  firstName: string;
  lastName: string;
  roles: string[];
  isActive: boolean;
  mustChangePassword: boolean;
  unlockAccount: boolean; // If true, resets failed login attempts
}

/**
 * Request to reset user password
 */
export interface ResetPasswordRequest {
  sendEmail: boolean;
}

/**
 * Response after password reset
 */
export interface ResetPasswordResponse {
  userId: number;
  temporaryPassword: string;
  message: string;
}

/**
 * Request to verify email (used in public route)
 */
export interface VerifyEmailRequest {
  userId: number;
  token: string;
}

/**
 * Response from email verification
 */
export interface VerificationResult {
  success: boolean;
  message: string;
  userId?: number;
  email?: string;
}

/**
 * Paginated response wrapper
 */
export interface PagedResult<T> {
  items: T[]; // Backend returns 'items', not 'data'
  page: number;
  pageSize: number;
  totalCount: number;
  totalPages: number;
  hasNextPage: boolean;
  hasPreviousPage: boolean;
}

/**
 * Filter parameters for user list query
 */
export interface UserListFilters {
  searchEmail?: string;
  role?: string | null;
  isActive?: boolean | null;
  emailConfirmed?: boolean | null;
}

/**
 * Pagination parameters
 */
export interface PaginationParams {
  page: number;
  pageSize: number;
}

/**
 * Combined query parameters for list users
 */
export interface ListUsersParams extends PaginationParams, UserListFilters {}

/**
 * Error response from API
 */
export interface ApiErrorResponse {
  code: string;
  message: string;
  details?: Record<string, string[]>;
}
